import Vue from 'vue'
import to from 'await-to-js'
import {
  fetchCurrentOrder,
  fetchCartItems,
  applyPromotionToCurrentOrder,
  removePromotionFromCurrentOrder,
  fetchAdjustmentFromCurrentOrder,
  completeCurrentOrder,
} from '@/components/Navigation/MiniCart/order-queries'
import Axios from '@/config/axios'
import { isEmpty } from '@/helpers/object'
import { isNumber } from '@/helpers/number'

export const ORDER_STATES = {
  cart: 'cart',
  address: 'address',
  delivery: 'delivery',
  payment: 'payment',
  confirm: 'confirm',
  complete: 'complete',
}

export const TAX_TYPES = {
  shipment: 'shipment',
}

export function itemTotal(items) {
  return items?.length > 0 ? items.map(i => i.price * i.quantity).reduce((sum, i) => sum + i) : 0
}

export function shippingTotal(
  items,
  shippingThreshold,
  shippingCost,
  shippingCostAboveThreshold,
  shippingCostBelowThreshold,
  selectedShippingRate
) {
  if (isEmpty(selectedShippingRate)) {
    if (shippingThreshold && isNumber(shippingThreshold)) {
      return itemTotal(items) >= shippingThreshold
        ? shippingCostAboveThreshold
        : shippingCostBelowThreshold
    } else {
      return shippingCost
    }
  } else {
    return parseFloat(selectedShippingRate.price || 0)
  }
}

export function promotionTotal(promotionAdjustment) {
  return parseFloat((promotionAdjustment && promotionAdjustment.amount) || 0)
}

export function promotionCode(promotionAdjustment) {
  return promotionAdjustment?.code || ''
}

export function taxLines(taxAdjustments) {
  return (taxAdjustments || []).map(({ label, amount }) => ({
    label,
    amount: parseFloat(amount),
  }))
}

export function taxTotal(taxAdjustments) {
  return (taxAdjustments || []).reduce((sum, { amount }) => sum + parseFloat(amount), 0)
}

export function totalWithoutStoreCreditsUsed(
  itemTotal,
  shippingTotal,
  promotionTotal,
  taxTotal,
  isTaxIncluded,
  canShowTaxLines
) {
  return (
    itemTotal + shippingTotal + promotionTotal + (isTaxIncluded || !canShowTaxLines ? 0 : taxTotal)
  )
}

export function total(totalWithoutStoreCreditsUsed, storeCreditsUsed) {
  return totalWithoutStoreCreditsUsed - storeCreditsUsed
}

const defaultState = {
  promotionAdjustment: {},
  taxAdjustments: [],
  isTaxIncluded: true,
  canShowTaxLines: true,
  dataIsInitialized: false,
  completedAt: null,
  containsSocks: false,
  cartItemIsLoading: false,
  adjustmentIsLoading: false,
  adyenTotalAmount: 0,
  quickAdding: false,
  items: [],
  number: '',
  mergedItemIndex: null,
  promoCode: '',
  promoDiscount: 0,
  paths: {
    termsAndConditionsUrl: '',
    privacyPolicyUrl: '',
  },
  shippingRates: [],
  isRegistrationValid: false, // TODO B2C-1129 remove in favour of state
  isShippingValid: false, // TODO B2C-1129 remove in favour of state
  isPaymentValid: false, // TODO B2C-1129 remove in favour of state
  state: 'cart',
  email: '',
  shippingAddress: {},
  billingAddress: {},
  accountShippingAddressId: '',
  accountBillingAddressId: '',
  isPreorder: false,
  preorderDeliveryDate: null,
  checkoutVersion: 1,
  errors: {},
}

export default {
  namespaced: true,
  state: { ...defaultState },
  mutations: {
    setState(state, payload) {
      for (const key in payload) {
        state[key] = payload[key]
      }
    },
    setPaths(state, paths) {
      for (const key in paths) {
        state.paths[key] = paths[key]
      }
    },
    unsetIsRegistrationValid(state) {
      state.isRegistrationValid = false
    },
    unsetIsShippingValid(state) {
      state.isShippingValid = false
    },
    setDataInitialized(state, payload = true) {
      state.dataIsInitialized = payload
    },
    addCartItem(state, payload) {
      state.items.unshift(payload)
      if (payload.isPreorderable) {
        state.isPreorder = true
      }
    },
    revertItemsOrder(state) {
      state.items.reverse()
    },
    setQuickAdding(state, payload) {
      state.quickAdding = payload
    },
    cartItemIsLoading(state, payload) {
      state.cartItemIsLoading = payload
    },
    adjustmentIsLoading(state, payload) {
      state.adjustmentIsLoading = payload
    },
    updateCartItemNumber(state, payload) {
      state.items[payload.index].quantity = payload.quantity
    },
    mergeCartItemNumber(state, payload) {
      state.mergedItemIndex = payload.index
      state.items[payload.index].quantity = state.items[payload.index].quantity + payload.increment
      setTimeout(() => (state.mergedItemIndex = null), On.TransitionTimes.slow)
    },
    setAdjustment(state, payload) {
      state.promotionAdjustment = payload
    },
    setShippingRates(state, payload) {
      state.shippingRates = payload
    },
    resetShippingRates(state) {
      state.shippingRates = []
    },
    setShippingAddress(state, payload) {
      state.shippingAddress = payload
    },
    removeBillingAddress(state) {
      state.billingAddress = {}
    },
    updateSelectedShippingRate(state, selectedShippingRateId) {
      state.shippingRates.forEach(({ id }, key) => {
        Vue.set(state.shippingRates[key], 'selected', id === selectedShippingRateId)
      })
      state.taxAdjustments = state.taxAdjustments.filter(obj => obj.type !== TAX_TYPES.shipment)
    },
  },
  actions: {
    async fetchData({ dispatch }) {
      const { currentOrder, paths } = await fetchCurrentOrder()
      dispatch('handleFetchData', { currentOrder, paths })
    },

    async completeOrder({ commit }) {
      const [error, response] = await to(completeCurrentOrder())
      if (error || response.completeCurrentOrder.errors)
        throw error || response.completeCurrentOrder.errors
      const data = response.completeCurrentOrder
      commit('setState', data)
    },

    resetState({ commit }) {
      commit('setState', { ...defaultState })
    },

    handleFetchData({ commit, rootState }, { currentOrder, paths }) {
      commit('setState', currentOrder)
      commit('setPaths', paths)
      commit('setDataInitialized')
      if (currentOrder?.items) commit('revertItemsOrder')

      if (currentOrder || rootState.recommendations) {
        const skus = currentOrder?.items?.map(item => item.sku) || []
        On.DynamicYield.updateRecommendation(skus)
      }
      window.dispatchEvent(new CustomEvent('on:order-ready'))
    },

    addCartItems({ state, commit }, payload) {
      // Show loader for missing cart item only if request takes more than one second
      const loadingTimer = setTimeout(() => {
        commit('cartItemIsLoading', true)
      }, 1000)

      // Don't show empty cart if product is being added but takes a while to show up
      commit('setQuickAdding', true)
      fetchCartItems(payload.ids).then(variants => {
        clearTimeout(loadingTimer)
        commit('cartItemIsLoading', false)
        variants.forEach(variant => {
          variant.variantId = variant.id
          variant.variantSku = variant.sku
          variant.sku = variant.productSku
          delete variant.id
          delete variant.productSku
          if (variant.quantity === undefined) variant.quantity = 1

          // If item already exists in cart merge it, else add as a new item
          const itemIndex = getItemIndex(state, {
            skuId: variant.variantId,
          })
          itemIndex === -1
            ? commit('addCartItem', variant)
            : commit('mergeCartItemNumber', {
              index: itemIndex,
              increment: 1,
            })
        })
        commit('setQuickAdding', false)
      })
    },

    updateCartItemNumber({ state, commit, dispatch }, payload) {
      commit('updateCartItemNumber', {
        index: getItemIndex(state, payload),
        quantity: payload.quantity,
      })
      if (!isEmpty(state.promotionAdjustment)) commit('adjustmentIsLoading', true)
      dispatch('updateServer', {
        skuId: payload.skuId,
        quantity: payload.increase,
        onDone: payload.onDone,
        onError: payload.onError,
      }).then(() => {
        dispatch('fetchAdjustment')
        window.dispatchEvent(new CustomEvent('on:update-cart'))
      })
    },

    updateServer({ commit, getters }, { skuId, quantity, onDone, onError }) {
      return Axios.post(getters.ordersPopulateUrl, {
        variant_id: skuId,
        quantity,
      })
        .then(response => {
          onDone && onDone()
          commit('setState', response.data.currentOrder) // makes sure order checkout version is updated
          return response.data
        })
        .catch(error => {
          if (error) {
            const message = error.response.data.errors.join(', ')
            onError && onError(message)
          }
        })
    },

    redirectToCheckout({ rootState }, { onError }) {
      return Axios.patch(`/${rootState.page.locale}/cart?checkout`, {})
        .then(function(response) {
          if (response.data.success) window.location.href = `/${rootState.page.locale}/checkout`
        })
        .catch(function(error) {
          const message = error.response.data.errors.join(', ')
          onError && onError(message)
        })
    },

    async applyPromoCode({ commit }, promoCode) {
      const [error, response] = await to(applyPromotionToCurrentOrder(promoCode))
      if (error) throw { type: 'error', text: '' }
      const data = response.applyPromotionToCurrentOrder
      if (data.messageType == 'success') {
        commit('setState', data.order)
        return { type: data.messageType, text: data.messageText }
      } else {
        throw { type: data.messageType, text: data.messageText }
      }
    },

    async clearPromoCode({ commit }, promoCode) {
      const [error, response] = await to(removePromotionFromCurrentOrder(promoCode))
      if (error) throw { type: 'error', text: '' }
      const data = response.removePromotionFromCurrentOrder
      commit('setState', data)
    },

    fetchAdjustment({ commit }) {
      fetchAdjustmentFromCurrentOrder().then(data => {
        commit('setAdjustment', data.currentOrder.promotionAdjustment)
        commit('adjustmentIsLoading', false)
      })
    },

    updateUserEmail({ commit }, email) {
      commit('setState', { email })
    },

    loginGuest({ state, dispatch }, { email }) {
      dispatch('updateUserEmail', email)
      const data = {
        order: {
          guest_checkout: true,
          email,
        },
      }
      return Axios.put(state.paths.updateCheckoutRegistrationUrl, data)
    },

    async removeAssociatedUser({ state, dispatch }) {
      const response = await Axios.get(state.paths.updateCheckoutRegistrationUrl)
      await dispatch('fetchData')
      return response
    },
  },

  getters: {
    cartItemIsLoading: state => state.cartItemIsLoading,
    adjustmentIsLoading: state => state.adjustmentIsLoading,
    quickAdding: state => state.quickAdding,
    dataIsInitialized: state => state.dataIsInitialized,
    items: state => state.items,
    itemsLength: state => state.items.length,
    isEmpty: state => state.items.length <= 0,
    itemsCount: state => state.items.reduce((sum, item) => sum + item.quantity, 0),
    itemTotal: state => (state.dataIsInitialized ? itemTotal(state.items) : 0),
    usedStoreCredit: (state, getters, rootState, rootGetters) =>
      Math.min(
        getters.totalWithoutStoreCreditsUsed,
        rootGetters['account/availableStoreCreditTotal']
      ),
    remainingStoreCredit: (state, getters, rootState, rootGetters) =>
      rootGetters['account/availableStoreCreditTotal'] - getters.usedStoreCredit,
    isStoreCreditUsed: (state, getters) => getters.usedStoreCredit > 0,
    shippingTotal: (state, getters, rootState, rootGetters) =>
      shippingTotal(
        state.items,
        rootGetters['store/shippingThreshold'],
        rootGetters['store/shippingCost'],
        rootGetters['store/shippingCostAboveThreshold'],
        rootGetters['store/shippingCostBelowThreshold'],
        state.shippingRates.find(obj => obj.selected)
      ),
    promotionTotal: state => promotionTotal(state.promotionAdjustment),
    promotionCode: state => promotionCode(state.promotionAdjustment),
    taxLines: state => taxLines(state.taxAdjustments),
    taxTotal: state => taxTotal(state.taxAdjustments),
    isTaxIncluded: state => state.isTaxIncluded,
    canShowTaxLines: state => state.canShowTaxLines,
    totalWithoutStoreCreditsUsed: (state, getters) =>
      totalWithoutStoreCreditsUsed(
        getters.itemTotal,
        getters.shippingTotal,
        getters.promotionTotal,
        getters.taxTotal,
        getters.isTaxIncluded,
        getters.canShowTaxLines
      ),
    total: (state, getters) => total(getters.totalWithoutStoreCreditsUsed, getters.usedStoreCredit),
    promoCode: state => state.promotionAdjustment && state.promotionAdjustment.code,
    promoDiscount: state => (state.promotionAdjustment && state.promotionAdjustment.amount) || 0,
    promotionAdjustment: state => state.promotionAdjustment,
    mergedItemIndex: state => state.mergedItemIndex,
    ordersPopulateUrl: (state, getters, rootState) => `/${rootState.page.locale}/orders/populate`,
    selectedShippingRateId: state => state.shippingRates?.find(obj => obj.selected)?.id || -1,
    itemOutOfStock: state => state.items.some(i => i.stock === 0 || i.quantity > i.stock),
    paths: state => state.paths,
    shippingAddress: state => state.shippingAddress,
    billingAddressId: state => state.billingAddress && state.billingAddress.id,
    canProcessPayment: state =>
      (state.state === ORDER_STATES.payment || state.state === ORDER_STATES.confirm) &&
      state.adyenTotalAmount > 0,
    canCompleteOrder: state =>
      (state.state === ORDER_STATES.payment || state.state === ORDER_STATES.confirm) &&
      state.adyenTotalAmount === 0,
    isPreorder: state => state.isPreorder,
  },
}

// Helper functions
function getItemIndex(state, payload) {
  return state.items.findIndex(item => {
    return Number(item.variantId) === payload.skuId
  })
}
